import React, { useEffect, useState } from 'react';
import { Link, withRouter, RouteComponentProps } from 'react-router-dom';
import { api } from '../../middleware/api';
import { END_POINTS } from '../../utils/constants/endPoints';
import ValidationErrorToast from '../../components/validationErrorToast';
import InterviewSidebar from './InterviewSidebar';

interface MatchParams { id: string; }

interface LocationState {
  job?: any;
}

const s: { [key: string]: React.CSSProperties } = {
  hero: {
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 40%, #0f3460 100%)',
    borderRadius: '16px',
    padding: '48px 40px',
    color: '#fff',
    minHeight: 'calc(100vh - 170px)',
    position: 'relative',
    overflow: 'hidden',
  },
  overlay: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.3) 100%)',
    pointerEvents: 'none' as any,
  },
  content: {
    position: 'relative',
    zIndex: 1,
    display: 'grid',
    gridTemplateColumns: '1fr 420px',
    gap: '48px',
    alignItems: 'center',
  },
  backLink: {
    color: 'rgba(255,255,255,0.7)',
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: 500,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    marginBottom: '32px',
    position: 'relative' as any,
    zIndex: 1,
  },
  heading: {
    fontSize: '48px',
    fontWeight: 300,
    lineHeight: 1.1,
    margin: '0 0 24px',
    color: '#fff',
    letterSpacing: '-0.02em',
    fontFamily: "'Inter', 'Helvetica Neue', sans-serif",
  },
  headingBold: {
    fontWeight: 700,
  },
  tagline: {
    fontSize: '36px',
    fontWeight: 300,
    fontStyle: 'italic',
    color: 'rgba(255,255,255,0.85)',
    margin: '0 0 24px',
    fontFamily: "'Georgia', serif",
  },
  subtitle: {
    color: 'rgba(255,255,255,0.7)',
    fontSize: '16px',
    lineHeight: 1.7,
    maxWidth: '540px',
    margin: '0 0 32px',
  },
  jobCard: {
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '12px',
    padding: '20px 24px',
  },
  jobTitle: {
    color: '#fff',
    margin: '0 0 6px',
    fontSize: '18px',
    fontWeight: 600,
  },
  jobText: {
    color: 'rgba(255,255,255,0.55)',
    margin: 0,
    fontSize: '14px',
    lineHeight: 1.6,
  },
  formCard: {
    background: '#ffffff',
    borderRadius: '16px',
    padding: '32px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
  },
  formTitle: {
    fontSize: '20px',
    fontWeight: 700,
    color: '#141414',
    margin: '0 0 4px',
  },
  formSubtitle: {
    fontSize: '14px',
    color: '#6B7280',
    margin: '0 0 24px',
  },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '12px 14px',
    border: '1px solid #D1D5DB',
    borderRadius: '8px',
    fontSize: '15px',
    outline: 'none',
    transition: 'border-color 0.15s',
    boxSizing: 'border-box' as any,
    color: '#111',
    background: '#F9FAFB',
  },
  hint: {
    fontSize: '12px',
    color: '#9CA3AF',
    marginTop: '4px',
  },
  uploadArea: {
    border: '2px dashed #D1D5DB',
    borderRadius: '10px',
    padding: '24px',
    textAlign: 'center' as any,
    cursor: 'pointer',
    background: '#F9FAFB',
    transition: 'border-color 0.15s',
  },
  uploadIcon: {
    display: 'block',
    fontSize: '14px',
    color: '#6B7280',
    marginBottom: '6px',
  },
  uploadText: {
    fontSize: '14px',
    color: '#374151',
    margin: 0,
  },
  uploadTypes: {
    fontSize: '12px',
    color: '#9CA3AF',
    marginTop: '4px',
    display: 'block',
  },
  submitBtn: {
    width: '100%',
    padding: '14px',
    background: '#141414',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    fontSize: '15px',
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    transition: 'background 0.15s',
  },
  footer: {
    textAlign: 'center' as any,
    fontSize: '12px',
    color: '#9CA3AF',
    marginTop: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
  },
  lockIcon: {
    display: 'inline-block',
    width: '12px',
    height: '12px',
  },
};

const LockSvg = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0110 0v4" />
  </svg>
);

const UploadSvg = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto 8px', display: 'block' }}>
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const FileSvg = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0065FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '6px', verticalAlign: 'middle' }}>
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

const InterviewStart: React.FC<RouteComponentProps<MatchParams, any, LocationState>> = ({ match, location }) => {
  const jobId = match.params.id;
  const [job, setJob] = useState<any>(location?.state?.job || null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [resume, setResume] = useState<File | null>(null);
  const [resumeText, setResumeText] = useState('');
  const [loading, setLoading] = useState(!location?.state?.job);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (location?.state?.job) {
      setJob(location.state.job);
      setLoading(false);
      return;
    }

    const loadJobFromList = async () => {
      try {
        const r = await api.get(END_POINTS.interviewJobs, {});
        const jobs = Array.isArray(r) ? r : (r?.results || []);
        const found = jobs.find((item: any) => String(item.id) === String(jobId));

        if (found) {
          setJob(found);
        } else {
          setJob({ id: jobId, title: 'Interview', description: '' });
          ValidationErrorToast('Warning', 'Could not find job details, but you can still start the interview.');
        }
      } catch (e: any) {
        setJob({ id: jobId, title: 'Interview', description: '' });
        ValidationErrorToast('Warning', 'Job details could not be loaded, but you can still start the interview.');
      } finally {
        setLoading(false);
      }
    };

    loadJobFromList();
  }, [jobId, location]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setResume(file);
      if (file.name.endsWith('.txt')) {
        const reader = new FileReader();
        reader.onload = (ev) => setResumeText(ev.target?.result as string || '');
        reader.readAsText(file);
      }
    }
  };

  const submitForm = (data: any, resumeValue?: string) => {
    const form = document.createElement('form');
    form.method = 'post';
    form.action = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
      ? 'http://localhost:3030/'
      : 'https://simulation.mploi.com/';
    form.target = '_blank';

    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'data';
    input.value = JSON.stringify(data);
    form.appendChild(input);

    if (resumeValue) {
      const resumeInput = document.createElement('input');
      resumeInput.type = 'hidden';
      resumeInput.name = 'resume';
      resumeInput.value = resumeValue;
      form.appendChild(resumeInput);
    }

    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
    setSubmitting(false);
  };

  const startInterview = () => {
    if (!name.trim()) return ValidationErrorToast('Required', 'Please enter your name');
    if (!email.trim()) return ValidationErrorToast('Required', 'Please enter your email');

    setSubmitting(true);

    const trimmedName = name.trim();
    const [firstName, ...lastNameParts] = trimmedName.split(' ');
    const employerName = job?.employer?.businessName || job?.employerName || job?.companyName || job?.businessName || 'MPLOI';

    const data: any = {
      job: {
        ...job,
        id: job?.id || jobId,
        title: job?.title || 'Interview',
        description: job?.description || '',
        employer: {
          ...(job?.employer || {}),
          businessName: employerName,
        },
      },
      employee: {
        user: {
          firstName: firstName || trimmedName,
          lastName: lastNameParts.join(' '),
          email: email.trim(),
          linkedinUrl: linkedinUrl.trim() || undefined,
        },
      },
      candidate: {
        name: trimmedName,
        email: email.trim(),
        linkedinUrl: linkedinUrl.trim(),
        resume: resumeText,
      },
    };

    if (resume && !resumeText) {
      const reader = new FileReader();
      reader.onload = (ev) => submitForm(data, ev.target?.result as string || '');
      reader.onerror = () => {
        setSubmitting(false);
        ValidationErrorToast('Resume Error', 'Could not read the resume file. Please try again.');
      };
      reader.readAsDataURL(resume);
    } else {
      submitForm(data);
    }
  };

  if (loading) {
    return (
      <InterviewSidebar>
        <div style={s.hero}>
          <div style={{ textAlign: 'center', padding: '80px 0' }}>
            <div className="spinner-border" role="status" style={{ color: '#fff' }}>
              <span className="sr-only">Loading...</span>
            </div>
            <p style={{ color: 'rgba(255,255,255,0.68)', marginTop: '20px' }}>Loading interview...</p>
          </div>
        </div>
      </InterviewSidebar>
    );
  }

  return (
    <InterviewSidebar>
      <div style={s.hero}>
        <div style={s.overlay} />

        <Link to="/interview-simulator/jobs" style={s.backLink}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
          Back to Jobs
        </Link>

        <div style={s.content}>
          <div>
            <h1 style={s.heading}>
              Welcome to your<br />
              <span style={s.headingBold}>AI Interview</span>
            </h1>
            <p style={s.tagline}>You've been invited to interview</p>
            <p style={s.subtitle}>
              You're about to take part in a realistic AI-powered interview for the role shown below. Our AI interviewer will ask you questions tailored to the position, assess your responses, and provide detailed feedback. The process takes around 15–30 minutes. Once complete, you'll receive a full performance report with strengths, areas for improvement, and an overall readiness rating.
            </p>

            {job?.title && (
              <div style={s.jobCard}>
                <h3 style={s.jobTitle}>{job.title}</h3>
                {job.description && <p style={s.jobText}>{job.description}</p>}
              </div>
            )}
          </div>

          <div style={s.formCard}>
            <h3 style={s.formTitle}>Start Your Interview</h3>
            <p style={s.formSubtitle}>Get interview-ready in 30 minutes</p>

            <div style={{ marginBottom: '16px' }}>
              <label style={s.label}>Full Name *</label>
              <input
                type="text"
                placeholder="Enter your full name"
                value={name}
                onChange={e => setName(e.target.value)}
                style={s.input}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={s.label}>Email Address *</label>
              <input
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                style={s.input}
              />
              <p style={s.hint}>We'll send your detailed performance report here</p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={s.label}>LinkedIn Profile URL</label>
              <input
                type="url"
                placeholder="https://www.linkedin.com/in/your-profile"
                value={linkedinUrl}
                onChange={e => setLinkedinUrl(e.target.value)}
                style={s.input}
              />
              <p style={s.hint}>Optional — helps personalize the interview and report.</p>
            </div>

            <div style={{ marginBottom: '8px' }}>
              <label style={s.label}>Upload Your Resume</label>
              <div style={s.uploadArea} onClick={() => document.getElementById('interview-start-resume-upload')?.click()}>
                <input
                  type="file"
                  id="interview-start-resume-upload"
                  accept=".pdf,.doc,.docx,.txt"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
                {resume ? (
                  <p style={{ ...s.uploadText, color: '#0065FF' }}><FileSvg />{resume.name}</p>
                ) : (
                  <>
                    <UploadSvg />
                    <p style={s.uploadText}>Drop your resume here or click to browse</p>
                    <span style={s.uploadTypes}>PDF, DOC, DOCX, TXT (Max 10MB)</span>
                  </>
                )}
              </div>
            </div>

            <button
              type="button"
              style={{
                ...s.submitBtn,
                opacity: submitting ? 0.7 : 1,
              }}
              onClick={startInterview}
              disabled={submitting}
            >
              {submitting ? 'Launching Interview...' : <>Prepare for interview <span style={{ fontSize: '18px' }}>→</span></>}
            </button>

            <div style={s.footer}>
              <LockSvg /> Your data is encrypted and never shared
            </div>
          </div>
        </div>
      </div>
    </InterviewSidebar>
  );
};

export default withRouter(InterviewStart);
